import { registerDecorator } from 'class-validator';
/**
 * Institutionskennzeichen (IK) — the nine-digit identifier a Leistungserbringer
 * signs a §302 file with. GR-IK Nr. 1.2.1 gives the structure:
 *
 * ```
 * 12 34 5678 9
 * │  │  │    └── Prüfziffer   (Stelle 9, über den Stellen 3–8)
 * │  │  └─────── Seriennummer (Stellen 5–8)
 * │  └────────── Regionalbereich (Stellen 3–4)
 * └───────────── Klassifikation  (Stellen 1–2) — die Berufsgruppe
 * ```
 *
 * Two checks live here, and the second one is the one that earns its keep.
 * The Prüfziffer runs over **Stellen 3–8 only**, and A1 §5.5 builds the logical
 * filename from **Stellen 3–8 only** — so neither mechanism can see the
 * Klassifikation, and a `66…` Abrechnungsstelle IK passes both while being
 * flatly wrong for a Heilmittel practice. The whitelist on Stellen 1–2 is the
 * only place a misclassified IK is detectable before the Kasse rejects the
 * whole file (§125-Vertrag §17 Abs. 3).
 */
/** Length of an IK, GR-IK Nr. 1.2.1. */
export const IK_LENGTH = 9;
/**
 * Klassifikationen (IK Stellen 1–2) a German Heilmittelerbringer can legitimately
 * hold, from GR-IK Anlage 1 "Klassifikationen und deren Geltungsbereiche":
 *
 * - `39` Podologen / sonstige therapeutische Hilfspersonen
 * - `40` Logopäden, Sprachheilbehandler, Sonderschullehrer
 * - `43` Med. Bademeister, Masseure, Praxen für physikalische Therapie
 * - `44` Krankengymnasten, Physiotherapeuten, Praxen für Physiotherapie
 * - `48` Beschäftigungstherapeuten, Ergotherapie, Künstlerische Therapie
 * - `49` sonstige therapeutische Hilfspersonen
 *
 * Deliberately excluded, and the reason this list exists: `66` Abrechnungsstellen /
 * Rechenzentren and `68` Softwarehersteller. Both are real IK Klassifikationen a
 * practice may have seen on paperwork, and both produce a structurally valid IK.
 *
 * **Flat, not per-Heilmittelbereich.** Both primary sources describe exactly one
 * list — a Klassifikations-Whitelist über die ersten zwei Stellen — and neither
 * pins a Klassifikation to a Bereich; the one worked recommendation is the flat
 * set `{40,43,44,48,49}` (plus `39` for Podologie, which that sentence omits and
 * the Klassifikationstabelle carries). Narrowing per Bereich would mean guessing
 * where the sources are silent, and a wrongly rejected IK blocks onboarding
 * outright — strictly worse than letting the Kasse reject a wrong one.
 */
export const HEILMITTEL_IK_KLASSIFIKATIONEN = ['39', '40', '43', '44', '48', '49'];
/**
 * Strips the decoration a practice pastes in — GR-IK Nr. 3.3 prints the IK as
 * `IK = 260326822` on invoices, and Zulassungsbescheide group the digits.
 * Returns the bare digits; validity is a separate question.
 */
export function normalizeIk(value) {
    return (value ?? '').replace(/[^0-9]/g, '');
}
/** Stellen 1–2 of a normalized IK — die Klassifikation / Berufsgruppe. */
export function ikKlassifikation(value) {
    return normalizeIk(value).slice(0, 2);
}
/**
 * The mod-10 Prüfziffer of an IK, GR-IK Nr. 1.2.5.
 *
 * Over **Stellen 3–8 only**: weight 1,2,1,2,1,2 from the right, replace each
 * product by its Quersumme, sum, take `mod 10`. There is no "10 minus remainder"
 * step and no modulo-11 — this is Luhn-like but not Luhn.
 *
 * Returns `null` when the input is not nine digits.
 */
export function computeIkPruefziffer(value) {
    const normalized = normalizeIk(value);
    if (normalized.length !== IK_LENGTH)
        return null;
    const body = normalized.slice(2, 8);
    let sum = 0;
    for (let i = 0; i < body.length; i++) {
        // Weights run 1,2,1,2,1,2 from the right, so the rightmost digit of the
        // six-digit body carries weight 1.
        const weight = (body.length - 1 - i) % 2 === 0 ? 1 : 2;
        const product = Number(body[i]) * weight;
        sum += product > 9 ? Math.floor(product / 10) + (product % 10) : product;
    }
    return sum % 10;
}
/** Whether Stelle 9 matches the Prüfziffer computed over Stellen 3–8. */
export function isIkPruefzifferValid(value) {
    const normalized = normalizeIk(value);
    const expected = computeIkPruefziffer(normalized);
    if (expected === null)
        return false;
    return Number(normalized[8]) === expected;
}
/** Whether a Klassifikation is one a Heilmittelerbringer can hold. */
export function isHeilmittelKlassifikation(klassifikation) {
    return HEILMITTEL_IK_KLASSIFIKATIONEN.includes(klassifikation);
}
/**
 * The single definition of "is this IK acceptable", returning *why* it is not so
 * that no caller has to re-run the cascade to produce a message.
 */
export function ikProblem(value) {
    const normalized = normalizeIk(value);
    if (normalized.length !== IK_LENGTH)
        return 'LENGTH';
    if (!isIkPruefzifferValid(normalized))
        return 'PRUEFZIFFER';
    if (!isHeilmittelKlassifikation(ikKlassifikation(normalized)))
        return 'BERUFSGRUPPE';
    return null;
}
/**
 * Nine digits, correct Prüfziffer over Stellen 3–8, and a Klassifikation on the
 * Heilmittel whitelist.
 */
export function isIkValid(value) {
    return ikProblem(value) === null;
}
/**
 * The same cascade as `ikProblem`, with the Berufsgruppe
 * verdict tolerated — shape and Prüfziffer are the whole rule for an Absender.
 *
 * ⚠ **No Berufsgruppen-Whitelist, and that is deliberate.** An Absender may be a
 * Klassifikation-`66` Abrechnungsstelle — the very thing the whitelist above
 * exists to keep off a Zulassung — and a practice billing through a shared
 * Abrechnungsstelle has an Absender that is on nobody's Zulassung at all.
 *
 * ⚠ **It delegates rather than re-running the checks.** The cascade returns
 * `BERUFSGRUPPE` only once length and Prüfziffer are already fine, so tolerating
 * exactly that one verdict *is* "everything but the whitelist" — and the two
 * rules cannot drift apart, because there is still only one of them.
 */
export function absenderIkProblem(value) {
    const problem = ikProblem(value);
    return problem === 'BERUFSGRUPPE' ? null : problem;
}
/** Nine digits and a correct Prüfziffer, whatever the Klassifikation says. */
export function isAbsenderIkValid(value) {
    return absenderIkProblem(value) === null;
}
export function IsValidIk(validationOptions) {
    return function (object, propertyName) {
        registerDecorator({
            name: 'isValidInstitutionskennzeichen',
            target: object.constructor,
            propertyName,
            options: {
                message: 'Institutionskennzeichen ist ungültig (9 Ziffern, Prüfziffer über Stellen 3–8, Berufsgruppe in Stellen 1–2)',
                ...validationOptions,
            },
            validator: {
                validate(value) {
                    return typeof value === 'string' && isIkValid(value);
                },
            },
        });
    };
}
export function IsValidAbsenderIk(validationOptions) {
    return function (object, propertyName) {
        registerDecorator({
            name: 'isValidAbsenderIk',
            target: object.constructor,
            propertyName,
            options: {
                message: 'Absender-IK ist ungültig (9 Ziffern, Prüfziffer über Stellen 3–8). Die Berufsgruppe wird hier nicht geprüft — ein Absender darf eine Abrechnungsstelle sein.',
                ...validationOptions,
            },
            validator: {
                validate(value) {
                    return typeof value === 'string' && isAbsenderIkValid(value);
                },
            },
        });
    };
}
//# sourceMappingURL=institutionskennzeichen.js.map